import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  type FormControlProps,
  type RadioGroupProps,
} from "@mui/material";
import { useId } from "react";
import { APP_FORM_DEFAULTS } from "./constants";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

export type AppRadioOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type Props = Omit<RadioGroupProps, "onChange"> & {
  label: string;
  options: AppRadioOption[];
  helperText?: string;
  error?: boolean;
  formControlProps?: Omit<FormControlProps, "children" | "error">;
  onChange: (value: string) => void;
};

/** Standard MUI radio group for dialog and form fields. */
export default function AppRadioGroup({
  label,
  options,
  helperText,
  error,
  formControlProps,
  row = true,
  value,
  onChange,
  ...radioGroupProps
}: Props) {
  const labelId = useId();
  const custom = useAppCustomStyles();
  return (
    <FormControl
      component="fieldset"
      fullWidth={APP_FORM_DEFAULTS.fullWidth}
      margin={APP_FORM_DEFAULTS.margin}
      error={error}
      {...formControlProps}
    >
      <FormLabel component="legend" id={labelId} sx={custom.components.appRadioGroup.legend}>
        {label}
      </FormLabel>
      <RadioGroup
        row={row}
        aria-labelledby={labelId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...radioGroupProps}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
            control={<Radio size="small" />}
            label={opt.label}
          />
        ))}
      </RadioGroup>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}
